/**
 * RoiInputsForm — the editable input side of the calculator, rendered from FIELD_GROUPS. Pure and
 * controlled: it owns no state, just reflects `inputs` and reports edits through `onChange`. The
 * value the engine consumes is always a number; a percent field is shown ×100 and divided back on
 * edit, and an empty/NaN entry coerces to 0 so computeRoi never receives a non-number.
 */
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import type { RoiInputs } from '../../lib/roi/roiModel'
import type { ChargerType } from '../../lib/roi/presets'
import { FIELD_GROUPS, stepFor, type FieldSpec } from './fields'
import { benchmarkHint } from './benchmarkHints'

export interface RoiInputsFormProps {
  inputs: RoiInputs
  chargerType: ChargerType
  onChange: (key: keyof RoiInputs, value: number) => void
}

function displayValue(spec: FieldSpec, raw: number): number {
  // Percent fields are stored as decimals (0.08) but edited as percents (8). Round to avoid binary
  // float noise (0.08 × 100 = 8.000000000000002) leaking into the field.
  return spec.pct ? Number((raw * 100).toFixed(4)) : raw
}

export function RoiInputsForm({ inputs, chargerType, onChange }: RoiInputsFormProps) {
  return (
    <div className="space-y-4">
      {FIELD_GROUPS.map((group) => (
        <fieldset key={group.title} className="space-y-2">
          <legend className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted-foreground">
            {group.title}
          </legend>
          <div className="grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
            {group.fields.map((spec) => {
              const hint = spec.benchmark ? benchmarkHint(spec.benchmark, chargerType) : null
              return (
                <div key={spec.key} className="flex flex-col gap-0.5">
                  <Label htmlFor={spec.key} className="text-xs text-secondary-foreground">
                    {spec.label}
                  </Label>
                  <div className="flex items-center gap-1.5">
                    <Input
                      id={spec.key}
                      type="number"
                      inputMode="decimal"
                      step={stepFor(spec, chargerType)}
                      value={displayValue(spec, inputs[spec.key])}
                      onChange={(e) => {
                        const raw = e.target.value
                        const n = raw === '' ? 0 : Number(raw)
                        if (!Number.isFinite(n)) return
                        onChange(spec.key, spec.pct ? n / 100 : n)
                      }}
                      className="h-8 text-sm"
                    />
                    {spec.unit && (
                      <span className="w-14 shrink-0 text-xs text-muted-foreground">
                        {spec.unit}
                      </span>
                    )}
                  </div>
                  {hint && (
                    <span className="text-[0.68rem] leading-tight text-muted-foreground">
                      Typical: {hint}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </fieldset>
      ))}
    </div>
  )
}
